"use client";

import { ReactNode, createContext, isValidElement, useContext } from "react";
import { csx } from "../../utils";
import { Button, type ButtonProps } from "../Button/Button";

import "./Banner.css";

//TODO: move svgs to lib and use Icon component
export const BANNER_ICONS = {
  success: (
    <svg viewBox="0 0 20 20" height="18" width="18">
      <path
        fillRule="evenodd"
        d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.857-9.809a.75.75 0 00-1.214-.882l-3.483 4.79-1.88-1.88a.75.75 0 10-1.06 1.061l2.5 2.5a.75.75 0 001.137-.089l4-5.5z"
      ></path>
    </svg>
  ),
  warning: (
    <svg viewBox="0 0 56 56" height="17" width="17">
      <path d="M9.59 50.207h36.82c3.516 0 5.719-2.531 5.719-5.719a5.56 5.56 0 0 0-.75-2.812l-18.445-33c-1.055-1.899-2.977-2.883-4.922-2.883c-1.922 0-3.89.984-4.946 2.883L4.645 41.699c-.516.89-.774 1.828-.774 2.79c0 3.187 2.227 5.718 5.719 5.718m18.422-16.055c-1.242 0-1.922-.703-1.969-1.968l-.328-11.578c-.047-1.266.937-2.204 2.273-2.204c1.313 0 2.344.961 2.297 2.227l-.351 11.555c-.047 1.289-.727 1.968-1.922 1.968m0 8.649c-1.36 0-2.625-1.078-2.625-2.532s1.242-2.53 2.625-2.53s2.625 1.054 2.625 2.53c0 1.477-1.266 2.532-2.625 2.532" />
    </svg>
  ),
  danger: (
    <svg viewBox="0 0 20 20" height="18" width="18">
      <path
        fillRule="evenodd"
        d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.28 7.22a.75.75 0 00-1.06 1.06L8.94 10l-1.72 1.72a.75.75 0 101.06 1.06L10 11.06l1.72 1.72a.75.75 0 101.06-1.06L11.06 10l1.72-1.72a.75.75 0 00-1.06-1.06L10 8.94 8.28 7.22z"
      ></path>
    </svg>
  ),
};

export const BANNER_VARIANTS = {
  default: "default",
  success: "success",
  warning: "warning",
  danger: "danger",
} as const;
export type BannerVariant = "default" | "success" | "warning" | "danger";

const BannerContext = createContext<BannerVariant | undefined>(undefined);

interface BannerIconProps {
  /**
   * Optional custom icon or children for the icon area.
   */
  children?: ReactNode;
  /**
   * The icon to display in the banner.
   * Can be a boolean to conditionally show or hide, or a ReactNode to provide a custom icon.
   */
  icon?: boolean | ReactNode;
  /**
   * The data-testid to display within the banner.
   */
  "data-testid"?: string;
}

interface BannerProps extends BannerIconProps {
  /**
   * Compact padding around the content of the banner.
   * @default true
   */
  compact?: boolean;
  /**
   * The content to display within the banner.
   */
  children: ReactNode;
  /**
   * The data-testid to display within the banner.
   */
  "data-testid"?: string;
  /**
   * Round the corners of the banner.
   * @default true
   */
  rounded?: boolean;
  /**
   * The banner's visual aesthetic.
   * - type {@link BannerVariant}
   */
  variant?: BannerVariant;
}

/**
 * A banner used to display a success, warning, or error message.
 *
 * API:
 *  - {@link BannerProps}
 *  - extends {@link BannerIconProps}
 */
const Banner = ({
  variant = "default",
  rounded = true,
  icon = false,
  compact = true,
  children,
  "data-testid": dataTestId,
}: BannerProps) => {
  return (
    <BannerContext.Provider value={variant}>
      <div
        role="status"
        aria-live="polite"
        className={csx(
          "proton-Banner",
          `proton-Banner--${variant}`,
          rounded && "proton-Banner--rounded"
        )}
        data-testid={dataTestId}
      >
        <div
          className={csx(
            "proton-Banner__wrapper",
            compact && "proton-Banner__wrapper--compact"
          )}
        >
          <Banner.Icon icon={icon} />
          <div className="proton-Banner__content-wrapper">
            <div className="proton-Banner__container">{children}</div>
          </div>
        </div>
      </div>
    </BannerContext.Provider>
  );
};

Banner.displayName = "Banner";

const BannerTitle = ({
  children,
  "data-testid": dataTestId,
}: {
  children: ReactNode;
  "data-testid"?: string;
}) => {
  const variant = useContext(BannerContext);
  if (!variant) throw new Error("BannerTitle must be used within a Banner");

  return (
    <h3
      className={csx(
        "proton-Banner__title",
        `proton-Banner--${variant}__title`
      )}
      data-testid={dataTestId}
    >
      {children}
    </h3>
  );
};

BannerTitle.displayName = "Banner.Title";

const BannerContent = ({
  children,
  "data-testid": dataTestId,
}: {
  children: ReactNode;
  "data-testid"?: string;
}) => {
  const variant = useContext(BannerContext);
  if (!variant) throw new Error("BannerContent must be used within a Banner");

  return (
    <p
      className={csx(
        "proton-Banner__content",
        `proton-Banner--${variant}__content`
      )}
      data-testid={dataTestId}
    >
      {children}
    </p>
  );
};

BannerContent.displayName = "Banner.Content";

function getIconContent(icon: ReactNode, variant: BannerVariant) {
  if (isValidElement(icon)) return icon;
  if (icon) return BANNER_ICONS[variant];
  return null;
}

const BannerIcon = ({ icon, "data-testid": dataTestId }: BannerIconProps) => {
  const variant = useContext(BannerContext);
  if (!variant) throw new Error("BannerIcon must be used within a Banner");

  const iconContent = getIconContent(icon, variant);
  if (!iconContent) return null;

  return (
    <div
      aria-hidden="true"
      data-testid={dataTestId}
      className={csx("proton-Banner__icon", `proton-Banner--${variant}__icon`)}
    >
      {iconContent}
    </div>
  );
};

BannerIcon.displayName = "Banner.Icon";

const BannerAction = ({ children, ...buttonProps }: ButtonProps) => {
  return <Button {...buttonProps}>{children}</Button>;
};

BannerAction.displayName = "Banner.Action";

const BannerActions = ({ children }: { children: ReactNode }) => {
  return <div className="proton-Banner__actions">{children}</div>;
};

BannerActions.displayName = "Banner.Actions";

/**
 * Renders an h3 styled banner.
 */
Banner.Title = BannerTitle;

/**
 * Renders a p styled banner.
 */
Banner.Content = BannerContent;

/**
 * Renders an icon inline with the banner title.
 *
 * API:
 *  - {@link BannerIconProps}
 */
Banner.Icon = BannerIcon;

/**
 * Renders a Proton Button.
 * - @prop buttonProps {@link ButtonProps}
 */
Banner.Action = BannerAction;

/**
 * Renders Proton Buttons in a responsive actions grid.
 */
Banner.Actions = BannerActions;

export { Banner };
